import React from 'react';
import {
  Typography,
  Box,
  Grid,
  LinearProgress,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Styled components for NEXUS design
const MetricsContainer = styled(Box)(({ theme }) => ({
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  padding: '25px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(57, 255, 20, 0.2)',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

const MetricsTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#39FF14',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '20px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  textShadow: '0 0 10px rgba(57, 255, 20, 0.5)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    marginBottom: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    marginBottom: '10px',
  },
}));

const MetricCard = styled(Box)(({ theme }) => ({
  border: '1px solid #6B6A78',
  backgroundColor: 'rgba(11, 8, 19, 0.4)',
  padding: '15px',
  borderRadius: '5px',
  marginBottom: '15px',
  [theme.breakpoints.down('md')]: {
    padding: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
  },
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  textTransform: 'uppercase',
  marginBottom: '5px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '18px',
  fontWeight: 'bold',
  [theme.breakpoints.down('md')]: {
    fontSize: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

const MetricSubtext = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '10px',
  marginTop: '5px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px',
  },
}));

const MetricsDivider = styled(Divider)(({ theme }) => ({
  borderColor: '#6B6A78',
  opacity: 0.3,
  margin: '15px 0',
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: '6px',
  borderRadius: '3px',
  backgroundColor: 'rgba(107, 106, 120, 0.3)',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#39FF14',
    boxShadow: '0 0 10px rgba(57, 255, 20, 0.5)',
  },
}));

const HistogramContainer = styled(Box)(({ theme }) => ({
  border: '1px solid #6B6A78',
  backgroundColor: 'rgba(11, 8, 19, 0.4)',
  padding: '15px',
  borderRadius: '5px',
  marginTop: '15px',
  [theme.breakpoints.down('md')]: {
    padding: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
  },
}));

interface MetricsPanelProps {
  metrics: any;
}

const MetricsPanel: React.FC<MetricsPanelProps> = ({ metrics }) => {
  if (!metrics) {
    return (
      <MetricsContainer>
        <MetricsTitle>WORLD METRICS</MetricsTitle>
        <MetricLabel>// NO METRICS AVAILABLE. ENCODE AN IMAGE TO SEE ANALYSIS RESULTS.</MetricLabel>
      </MetricsContainer>
    );
  }

  const prepareHistogramData = () => {
    if (!metrics.histograms) return null;

    const datasets: any[] = [];
    const colors = {
      red: 'rgb(255, 99, 132)',
      green: 'rgb(75, 192, 192)',
      blue: 'rgb(54, 162, 235)',
      gray: 'rgb(153, 102, 255)'
    };

    Object.keys(metrics.histograms).forEach((channel) => {
      const channelData = metrics.histograms[channel];
      
      datasets.push({
        label: `${channel.charAt(0).toUpperCase() + channel.slice(1)} (Original)`,
        data: channelData.original.slice(0, 256), // Limit to 256 bins
        borderColor: colors[channel as keyof typeof colors] || 'rgb(201, 203, 207)',
        backgroundColor: `${colors[channel as keyof typeof colors] || 'rgb(201, 203, 207)'}20`,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        borderWidth: 2
      });

      datasets.push({
        label: `${channel.charAt(0).toUpperCase() + channel.slice(1)} (Encoded)`,
        data: channelData.encoded.slice(0, 256),
        borderColor: colors[channel as keyof typeof colors] || 'rgb(201, 203, 207)',
        backgroundColor: `${colors[channel as keyof typeof colors] || 'rgb(201, 203, 207)'}10`,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        borderWidth: 1
      });
    });

    return {
      labels: Array.from({ length: 256 }, (_, i) => i),
      datasets
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 10
          }
        }
      },
      title: {
        display: true,
        text: 'RGB Histogram Comparison',
        color: 'rgba(255, 255, 255, 0.9)',
        font: {
          size: 14
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Pixel Value',
          color: 'rgba(255, 255, 255, 0.7)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Frequency',
          color: 'rgba(255, 255, 255, 0.7)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  const histogramData = prepareHistogramData();

  return (
    <MetricsContainer>
      <MetricsTitle>WORLD METRICS</MetricsTitle>

      <Grid container spacing={{ xs: 1, md: 2 }}>
        {/* Quality Rating */}
        <Grid item xs={12}>
          <MetricCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <MetricLabel>OVERALL QUALITY</MetricLabel>
              <MetricValue sx={{ color: '#39FF14' }}>{metrics.quality_rating}</MetricValue>
            </Box>
          </MetricCard>
        </Grid>

        <Grid item xs={12}>
          <MetricsDivider />
        </Grid>

        {/* PSNR */}
        <Grid item xs={6}>
          <MetricCard>
            <MetricLabel>PSNR (PEAK SIGNAL-TO-NOISE RATIO)</MetricLabel>
            <MetricValue>
              {metrics.psnr === Infinity ? '∞' : metrics.psnr.toFixed(2)} DB
            </MetricValue>
            <ProgressBar
              variant="determinate"
              value={Math.min((metrics.psnr / 50) * 100, 100)}
              sx={{ mt: 1 }}
            />
          </MetricCard>
        </Grid>

        {/* MSE */}
        <Grid item xs={6}>
          <MetricCard>
            <MetricLabel>MSE (MEAN SQUARED ERROR)</MetricLabel>
            <MetricValue>{metrics.mse.toFixed(6)}</MetricValue>
            <ProgressBar
              variant="determinate"
              value={Math.max(0, 100 - (metrics.mse * 1000))}
              sx={{ mt: 1 }}
            />
          </MetricCard>
        </Grid>

        {/* SSIM (if available) */}
        {metrics.ssim !== undefined && (
          <Grid item xs={6}>
            <MetricCard>
              <MetricLabel>SSIM (STRUCTURAL SIMILARITY)</MetricLabel>
              <MetricValue>{metrics.ssim.toFixed(4)}</MetricValue>
              <ProgressBar
                variant="determinate"
                value={metrics.ssim * 100}
                sx={{ mt: 1 }}
              />
            </MetricCard>
          </Grid>
        )}

        {/* File Size Impact */}
        <Grid item xs={12}>
          <MetricCard>
            <MetricLabel>FILE SIZE IMPACT</MetricLabel>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={4}>
                <MetricSubtext>ORIGINAL</MetricSubtext>
                <MetricValue>{(metrics.file_size_impact.original_size / 1024).toFixed(1)} KB</MetricValue>
              </Grid>
              <Grid item xs={4}>
                <MetricSubtext>ENCODED</MetricSubtext>
                <MetricValue>{(metrics.file_size_impact.encoded_size / 1024).toFixed(1)} KB</MetricValue>
              </Grid>
              <Grid item xs={4}>
                <MetricSubtext>INCREASE</MetricSubtext>
                <MetricValue sx={{ color: '#39FF14' }}>+{metrics.file_size_impact.percentage_increase.toFixed(2)}%</MetricValue>
              </Grid>
            </Grid>
          </MetricCard>
        </Grid>

        {/* Histogram Correlation */}
        {metrics.histogram_correlation && (
          <Grid item xs={12}>
            <MetricCard>
              <MetricLabel>HISTOGRAM CORRELATION</MetricLabel>
              <Grid container spacing={1} sx={{ mt: 2 }}>
                {Object.entries(metrics.histogram_correlation).map(([channel, correlation]: [string, any]) => (
                  <Grid item xs={4} key={channel}>
                    <MetricSubtext>{channel.charAt(0).toUpperCase() + channel.slice(1)}</MetricSubtext>
                    <MetricValue>{correlation.toFixed(4)}</MetricValue>
                  </Grid>
                ))}
              </Grid>
            </MetricCard>
          </Grid>
        )}

        {/* Histogram Chart */}
        {histogramData && (
          <Grid item xs={12}>
            <HistogramContainer>
              <MetricLabel>HISTOGRAM COMPARISON</MetricLabel>
              <Box sx={{ mt: 2, height: 200 }}>
                <Line data={histogramData} options={chartOptions} />
              </Box>
            </HistogramContainer>
          </Grid>
        )}
      </Grid>
    </MetricsContainer>
  );
};

export default MetricsPanel;
